import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { UserProfile } from '@bcc/shared';
import { PhoneField } from './PhoneField';
import { apiFetch } from '../lib/api';
import { resolvePostLoginRoute } from '../lib/postLoginRoute';
import { supabase } from '../lib/supabase';

const labelCls =
  'font-display text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)]/90';

const inputCls =
  'mt-1.5 w-full rounded-xl border border-white/12 bg-white px-3 py-2.5 font-body text-sm text-neutral-900 placeholder:text-neutral-400 shadow-inner shadow-black/5 outline-none transition focus:border-[var(--home-miami-blue)] focus:ring-2 focus:ring-[var(--home-miami-blue)]/35';

const selectCls = `${inputCls} cursor-pointer`;

const requiredMark = <span className="text-red-500">*</span>;

const HEAR_ABOUT_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'referral', label: 'Referral' },
  { value: 'event', label: 'Event / webinar' },
  { value: 'search', label: 'Web search' },
  { value: 'social', label: 'Social media' },
  { value: 'other', label: 'Other' },
];

type Props = {
  id?: string;
  showLoginLink?: boolean;
};

export function RegisterForm({ id, showLoginLink = true }: Props) {
  const nav = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [commitment, setCommitment] = useState('');
  const [hearAbout, setHearAbout] = useState('');
  const [accredited, setAccredited] = useState<'yes' | 'no' | ''>('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkEmailOpen, setCheckEmailOpen] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendFeedback, setResendFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const closeCheckEmailModal = () => {
    setCheckEmailOpen(false);
    setResendFeedback(null);
  };

  async function resendConfirmationEmail() {
    if (!email.trim()) return;
    setResendLoading(true);
    setResendFeedback(null);
    const origin = window.location.origin;
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
      options: {
        emailRedirectTo: `${origin}/login?confirmed=1`,
      },
    });
    setResendLoading(false);
    if (error) {
      setResendFeedback({ ok: false, text: error.message });
      return;
    }
    setResendFeedback({ ok: true, text: 'Confirmation email sent again — check your inbox.' });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!accredited) {
      setErr('Please indicate whether you are an accredited investor.');
      return;
    }
    setLoading(true);
    const fullName = `${firstName} ${lastName}`.trim();
    const origin = window.location.origin;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${origin}/login?confirmed=1`,
        data: {
          full_name: fullName,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim() || null,
          desired_commitment: commitment.trim(),
          hear_about_us: hearAbout || null,
          accredited_investor: accredited === 'yes',
        },
      },
    });
    setLoading(false);
    if (error) {
      setErr(error.message);
      return;
    }
    /** Email confirmation enabled in Supabase → no session until user clicks the link. */
    if (data.session) {
      try {
        const me = await apiFetch<{ user: UserProfile }>('/api/me', { token: data.session.access_token });
        nav(resolvePostLoginRoute(undefined, me.user.role), { replace: true });
      } catch {
        nav('/dashboard', { replace: true });
      }
      return;
    }
    setCheckEmailOpen(true);
  }

  return (
    <>
      {checkEmailOpen ? (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center bg-[var(--home-zone-void)]/85 p-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="check-email-title"
          aria-describedby="check-email-desc"
        >
          <div className="relative w-full max-w-md rounded-2xl border border-white/[0.1] bg-[var(--home-zone-dark)] p-6 shadow-[0_32px_90px_-40px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.06]">
            <button
              type="button"
              onClick={closeCheckEmailModal}
              className="absolute right-3 top-3 rounded-lg p-2 text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] hover:text-[var(--home-text-on-dark)]"
              aria-label="Dismiss"
            >
              <span aria-hidden className="block text-lg leading-none">
                ×
              </span>
            </button>
            <p className="font-mono text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[var(--home-miami-blue)]">
              Almost there
            </p>
            <h2 id="check-email-title" className="mt-2 font-display text-xl font-bold text-[var(--home-text-on-dark)]">
              Confirm your email to activate your account
            </h2>
            <p id="check-email-desc" className="mt-3 font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
              We sent a message to <strong className="text-[var(--home-text-on-dark)]">{email}</strong>. Open it and tap{' '}
              <strong className="text-[var(--home-text-on-dark)]">Confirm your mail</strong> — then sign in on the next
              screen.
            </p>
            {resendFeedback ? (
              <p
                role="status"
                className={`mt-4 font-body text-sm ${resendFeedback.ok ? 'text-emerald-300/95' : 'text-rose-200'}`}
              >
                {resendFeedback.text}
              </p>
            ) : null}
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Link
                to="/login"
                className="inline-flex justify-center rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-center font-display text-[0.62rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] transition hover:bg-white/[0.08]"
                onClick={closeCheckEmailModal}
              >
                Go to sign in
              </Link>
              <button
                type="button"
                disabled={resendLoading}
                onClick={() => void resendConfirmationEmail()}
                className="inline-flex justify-center rounded-xl bg-[var(--home-miami-blue)] px-4 py-2.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.1em] text-[var(--home-zone-void)] shadow-md shadow-black/20 transition hover:brightness-105 disabled:opacity-50"
              >
                {resendLoading ? 'Sending…' : 'Resend email'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    <form id={id} className="space-y-4" onSubmit={onSubmit}>
      {err ? (
        <p className="rounded-xl border border-rose-400/40 bg-rose-950/40 px-3 py-2 font-body text-sm text-rose-100">{err}</p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className={`${labelCls} flex flex-col`}>
          First name {requiredMark}
          <input
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            className={inputCls}
            placeholder="Jane"
          />
        </label>
        <label className={`${labelCls} flex flex-col`}>
          Last name {requiredMark}
          <input
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            className={inputCls}
            placeholder="Investor"
          />
        </label>
      </div>

      <label className={`${labelCls} flex flex-col`}>
        Phone
        <PhoneField
          value={phone}
          onChange={setPhone}
          autoComplete="tel"
          placeholder="(555) 555-0000"
          className="mt-1.5"
          selectClassName={`${inputCls} mt-0 min-w-[7.25rem] shrink-0 cursor-pointer py-2.5 font-body text-xs`}
          inputClassName={`${inputCls} mt-0 min-w-0 flex-1`}
        />
      </label>

      <label className={`${labelCls} flex flex-col`}>
        Email {requiredMark}
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          className={inputCls}
          placeholder="you@example.com"
        />
      </label>

      <label className={`${labelCls} flex flex-col`}>
        Password {requiredMark}
        <input
          required
          type="password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          className={inputCls}
          placeholder="Minimum 8 characters"
        />
      </label>

      <div className="flex flex-col">
        <label className={`${labelCls} mb-0`}>
          Desired commitment {requiredMark}
        </label>
        <div className="mt-1.5 flex overflow-hidden rounded-xl border border-white/12 bg-white shadow-inner shadow-black/5 focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/35">
          <span className="flex shrink-0 items-center border-r border-white/20 bg-neutral-100 px-3 font-body text-sm font-medium text-neutral-600">
            $
          </span>
          <input
            required
            value={commitment}
            onChange={(e) => setCommitment(e.target.value)}
            className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 font-body text-sm text-neutral-900 placeholder:text-neutral-400 outline-none"
            placeholder="250,000"
            inputMode="decimal"
            autoComplete="off"
          />
        </div>
        <p className="mt-1.5 font-body text-xs text-[var(--home-muted-on-dark)]">
          Class A minimum $250,000 · Class B minimum $50,000
        </p>
      </div>

      <label className={`${labelCls} flex flex-col`}>
        How did you hear about us?
        <select value={hearAbout} onChange={(e) => setHearAbout(e.target.value)} className={selectCls}>
          {HEAR_ABOUT_OPTIONS.map((o) => (
            <option key={o.value || 'blank'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <fieldset className="border-0 p-0">
        <legend className={`${labelCls} mb-2 w-full`}>
          Are you an accredited investor? {requiredMark}
        </legend>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
          <label className="flex cursor-pointer items-center gap-2 font-body text-sm text-[var(--home-text-on-dark)]">
            <input
              type="radio"
              name="accredited"
              checked={accredited === 'yes'}
              onChange={() => setAccredited('yes')}
              className="h-4 w-4 border-white/25 text-[var(--home-miami-blue)] focus:ring-[var(--home-miami-blue)]"
            />
            Yes
          </label>
          <label className="flex cursor-pointer items-center gap-2 font-body text-sm text-[var(--home-text-on-dark)]">
            <input
              type="radio"
              name="accredited"
              checked={accredited === 'no'}
              onChange={() => setAccredited('no')}
              className="h-4 w-4 border-white/25 text-[var(--home-miami-blue)] focus:ring-[var(--home-miami-blue)]"
            />
            No
          </label>
        </div>
      </fieldset>

      <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:items-center">
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-[var(--home-miami-blue)] px-6 py-3 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-zone-void)] shadow-md shadow-black/15 transition hover:brightness-105 disabled:opacity-50 sm:w-auto"
        >
          {loading ? 'Submitting…' : 'Request access'}
        </button>
        <Link
          to="/"
          className="inline-flex w-full items-center justify-center rounded-xl bg-[var(--header-shell-bg)] px-6 py-3 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-white shadow-md shadow-black/15 ring-1 ring-white/25 transition hover:brightness-110 sm:w-auto"
        >
          Cancel
        </Link>
      </div>

      {showLoginLink ? (
        <p className="pt-2 text-center font-body text-[0.8125rem] text-[var(--home-muted-on-dark)]">
          Already onboard?{' '}
          <Link to="/login" className="font-medium text-[var(--home-miami-blue)] underline-offset-4 hover:underline">
            Sign in
          </Link>
        </p>
      ) : null}
    </form>
    </>
  );
}
